import {TreeNode} from '@hconnect/common/types'
import {Loader} from '@hconnect/uikit'
import {getIsKilnConfigValid} from '@kilnfeature/helpers'
import {Alert, Button, Link, Stack, Typography} from '@mui/material'
import {DrillingSettings} from '@refra/modules/refra-planning/components/KilnOverviewCard/Drilling/DrillingSettings'
import {useDrillingSettingsQuery} from '@refra/modules/refra-planning/hooks/queries/useDrillingSettingsQuery'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {useGetAssetConfig} from '@settings/modules/plant-setup/hooks/assetConfig/useGetAssetConfig'
import {useGetPlantUpmId} from '@settings/modules/plant-setup/hooks/upmEntityConfig/useGetPlantUpmId'
import {ItemDetailsLayout} from '@settings/modules/plant-setup/plant-setup-item-layout'
import {getUrl, PLANT_ROUTES, useUrlParam} from '@settings/modules/routing'

interface DrillingZonesProps {
  selectedItem: TreeNode
}

export const DrillingZones = ({selectedItem}: DrillingZonesProps) => {
  const {data: plant} = useGetPlantUpmId()
  const navigate = useNavigate()
  const plantCode = useUrlParam('plantCode')
  const {t} = useTranslation()

  const {data: drillingSettings, isLoading: isLoadingDrilling} = useDrillingSettingsQuery({
    plantUpmId: plant?.upmId ?? '',
    kilnUpmId: selectedItem.upmId
  })

  const {
    data: kilnConfig,
    isLoading: isLoadingAssetConfig,
    error: kilnConfigError
  } = useGetAssetConfig(selectedItem.upmId)

  if (isLoadingDrilling || isLoadingAssetConfig) return <Loader />

  const isMandatoryKilnConfigSetUp =
    !!kilnConfig &&
    kilnConfigError?.response?.status !== 404 &&
    getIsKilnConfigValid(kilnConfig.payload.layout, kilnConfig.payload.configuration)

  if (!drillingSettings)
    return (
      <ItemDetailsLayout>
        <Alert
          elevation={4}
          severity={'error'}
          sx={{
            borderRadius: '4px'
          }}
        >
          <Typography variant="body1">{t('plantSetup.drillingZone.missing')}</Typography>
        </Alert>
      </ItemDetailsLayout>
    )

  return (
    <ItemDetailsLayout>
      <Stack display="flex" flexDirection="column" gap={3} flexGrow={1}>
        {isMandatoryKilnConfigSetUp ? (
          <DrillingSettings drillingSettings={drillingSettings} kilnConfig={kilnConfig} />
        ) : (
          <Alert
            elevation={4}
            severity={'error'}
            sx={{
              borderRadius: '4px'
            }}
            action={
              <Button
                variant="contained"
                color="primary"
                size="small"
                component={Link}
                onClick={() =>
                  navigate(
                    getUrl(PLANT_ROUTES.KILN_CONFIG.path, {plantCode, kilnId: selectedItem.upmId})
                  )
                }
              >
                {t('kilnConfig.setUpKiln')}
              </Button>
            }
          >
            <Typography variant="body1">{t('kilnConfig.createKilnConfigAlert')}</Typography>
          </Alert>
        )}
      </Stack>
    </ItemDetailsLayout>
  )
}
